import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export const useInterfaceStore = defineStore('interface', () => {
    // Default widths
    const DEFAULT_LEFT_NAV_WIDTH = '20rem'
    const DEFAULT_RIGHT_NAV_WIDTH = '20rem'
    const DEFAULT_LOBBY_CHAT_WIDTH = '24rem'
    const DEFAULT_ETCH_LEFT_PANE_WIDTH = '16rem'
    const DEFAULT_ETCH_RIGHT_PANE_WIDTH = '20rem'

    // Interface settings
    const DEFAULT_VOICE_ACTIONS_ENABLED = true

    // Reactive refs for navigation widths
    const leftNavWidth = ref(DEFAULT_LEFT_NAV_WIDTH)
    const rightNavWidth = ref(DEFAULT_RIGHT_NAV_WIDTH)
    const lobbyChatWidth = ref(DEFAULT_LOBBY_CHAT_WIDTH)
    const voiceActionsEnabled = ref(DEFAULT_VOICE_ACTIONS_ENABLED)
    const etchLeftPaneWidth = ref(DEFAULT_ETCH_LEFT_PANE_WIDTH)
    const etchRightPaneWidth = ref(DEFAULT_ETCH_RIGHT_PANE_WIDTH)

    // Generation indicator position (loaded eagerly since the indicator is global)
    const savedPos = localStorage.getItem('tavern_generation_indicator_position')
    const generationIndicatorPosition = ref(savedPos ? JSON.parse(savedPos) : null)

    // LocalStorage keys
    const LEFT_NAV_KEY = 'tavern_left_nav_width'
    const RIGHT_NAV_KEY = 'tavern_right_nav_width'
    const LOBBY_CHAT_KEY = 'tavern_lobby_chat_width'
    const VOICE_ACTIONS_KEY = 'tavern_voice_actions_enabled'
    const ETCH_LEFT_PANE_KEY = 'tavern_etch_left_pane_width'
    const ETCH_RIGHT_PANE_KEY = 'tavern_etch_right_pane_width'
    const GENERATION_INDICATOR_POSITION_KEY = 'tavern_generation_indicator_position'

    // Initialize widths from localStorage
    function initializeWidths() {
        const savedLeftWidth = localStorage.getItem(LEFT_NAV_KEY)
        const savedRightWidth = localStorage.getItem(RIGHT_NAV_KEY)
        const savedLobbyChatWidth = localStorage.getItem(LOBBY_CHAT_KEY)
        const savedVoiceActions = localStorage.getItem(VOICE_ACTIONS_KEY)

        if (savedLeftWidth) {
            leftNavWidth.value = savedLeftWidth
        }

        if (savedRightWidth) {
            rightNavWidth.value = savedRightWidth
        }

        if (savedLobbyChatWidth) {
            lobbyChatWidth.value = savedLobbyChatWidth
        }

        if (savedVoiceActions !== null) {
            voiceActionsEnabled.value = JSON.parse(savedVoiceActions)
        }

        const savedEtchLeftWidth = localStorage.getItem(ETCH_LEFT_PANE_KEY)
        const savedEtchRightWidth = localStorage.getItem(ETCH_RIGHT_PANE_KEY)

        if (savedEtchLeftWidth) {
            etchLeftPaneWidth.value = savedEtchLeftWidth
        }

        if (savedEtchRightWidth) {
            etchRightPaneWidth.value = savedEtchRightWidth
        }

    }

    // Save left navigation width
    function setLeftNavWidth(width) {
        leftNavWidth.value = width
        localStorage.setItem(LEFT_NAV_KEY, width)
    }

    // Save right navigation width
    function setRightNavWidth(width) {
        rightNavWidth.value = width
        localStorage.setItem(RIGHT_NAV_KEY, width)
    }

    // Save lobby chat width
    function setLobbyChatWidth(width) {
        lobbyChatWidth.value = width
        localStorage.setItem(LOBBY_CHAT_KEY, width)
    }

    // Save voice actions enabled setting
    function setVoiceActionsEnabled(enabled) {
        voiceActionsEnabled.value = enabled
        localStorage.setItem(VOICE_ACTIONS_KEY, JSON.stringify(enabled))
    }

    // Save etch left pane width
    function setEtchLeftPaneWidth(width) {
        etchLeftPaneWidth.value = width
        localStorage.setItem(ETCH_LEFT_PANE_KEY, width)
    }

    // Save etch right pane width
    function setEtchRightPaneWidth(width) {
        etchRightPaneWidth.value = width
        localStorage.setItem(ETCH_RIGHT_PANE_KEY, width)
    }

    // Save generation indicator position
    function setGenerationIndicatorPosition(position) {
        generationIndicatorPosition.value = position
        if (position) {
            localStorage.setItem(GENERATION_INDICATOR_POSITION_KEY, JSON.stringify(position))
        } else {
            localStorage.removeItem(GENERATION_INDICATOR_POSITION_KEY)
        }
    }

    // Reset to default widths
    function resetWidths() {
        leftNavWidth.value = DEFAULT_LEFT_NAV_WIDTH
        rightNavWidth.value = DEFAULT_RIGHT_NAV_WIDTH
        lobbyChatWidth.value = DEFAULT_LOBBY_CHAT_WIDTH
        localStorage.removeItem(LEFT_NAV_KEY)
        localStorage.removeItem(RIGHT_NAV_KEY)
        localStorage.removeItem(LOBBY_CHAT_KEY)
    }

    // Watch for changes and auto-save (optional alternative to manual saving)
    watch(leftNavWidth, (newWidth) => {
        localStorage.setItem(LEFT_NAV_KEY, newWidth)
    })

    watch(rightNavWidth, (newWidth) => {
        localStorage.setItem(RIGHT_NAV_KEY, newWidth)
    })

    watch(lobbyChatWidth, (newWidth) => {
        localStorage.setItem(LOBBY_CHAT_KEY, newWidth)
    })

    watch(etchLeftPaneWidth, (newWidth) => {
        localStorage.setItem(ETCH_LEFT_PANE_KEY, newWidth)
    })

    watch(etchRightPaneWidth, (newWidth) => {
        localStorage.setItem(ETCH_RIGHT_PANE_KEY, newWidth)
    })

    return {
        leftNavWidth,
        rightNavWidth,
        lobbyChatWidth,
        voiceActionsEnabled,
        initializeWidths,
        setLeftNavWidth,
        setRightNavWidth,
        setLobbyChatWidth,
        setVoiceActionsEnabled,
        etchLeftPaneWidth,
        etchRightPaneWidth,
        setEtchLeftPaneWidth,
        setEtchRightPaneWidth,
        resetWidths,
        generationIndicatorPosition,
        setGenerationIndicatorPosition,
    }
})
